/**
 * SceQualityChip — SCE-FOLLOWUP-CLAIM-CHIPS.
 *
 * Surfaces the `qualityWarnings: ['sce-*']` tags stamped by SCE-4's lazy
 * migration (see `services/siteContext/sceLazyMigration.ts`) as an amber
 * dot + click-to-disclose tooltip on each Site Context claim row.
 *
 * Read-only — the chip never mutates the claim. The user adjudication
 * surface (`Mark as mis-extracted`) lives on the diff tab; this chip is
 * the awareness signal that drives the user to that adjudication.
 *
 * Tag mapping (kept narrow — exactly the set the lazy migrator and
 * fresh extractor stamp; see `SceQualityWarning` in
 * `src/lib/sed/fieldSchema.ts`):
 *
 *   - `sce-type-mismatch`        — value isn't the expected JS type for
 *                                  the SED field.
 *   - `sce-out-of-range`         — value is numerically plausible but
 *                                  outside the field's accepted envelope.
 *   - `sce-missing-unit-token`   — numeric value's source span has no
 *                                  recognised unit token (km/MW/acre…).
 *   - `sce-enum-mismatch`        — value doesn't appear in the field's
 *                                  closed enum.
 *   - `sce-cross-field`          — cross-field rule (e.g. state locus,
 *                                  substation ratio) flagged the claim.
 *
 * Unknown / forward-compat tags fall through to a generic "quality
 * concern" copy so future SCE-* tags surface without a UI patch.
 *
 * Composition with shipped surfaces:
 *
 *   - Consumed by `SiteContextInput` (the structured claim list under
 *     the prose textarea on both the new-site wizard and the rerun
 *     panel).
 *   - Tooltip is rendered as a native `<details>` + summary so it works
 *     without a portal / popper dependency and keeps the chip accessible
 *     via keyboard (Enter on summary toggles the disclosure).
 */
import React from 'react';
import type { SceQualityWarning } from '../../lib/sed/fieldSchema';

/**
 * Human-readable reason per SCE-* tag. Kept in this module (not in
 * `fieldSchema.ts`) because it's UI copy — the schema layer owns the
 * stable telemetry codes, the UI layer owns the user-facing English.
 */
const QUALITY_WARNING_REASONS: Record<SceQualityWarning, string> = {
  'sce-type-mismatch':
    'Value type doesn\'t match the expected SED field (e.g. a number where a string is expected).',
  'sce-out-of-range':
    'Numeric value is outside the plausible range for this field.',
  'sce-missing-unit-token':
    'Numeric value has no unit token (km/MW/acre…) in its supporting prose — the unit can\'t be confirmed.',
  'sce-enum-mismatch':
    'Value isn\'t in the closed enum for this field (e.g. a state code that doesn\'t exist).',
  'sce-cross-field':
    'A cross-field rule (state locus / substation ratio / distance ratio) flagged this claim.',
  // SCE-FOLLOWUP-PROBABILISTIC-DISAMBIGUATION (SX-2). Surfaced when the
  // extractor's confidence was below the disambiguation band (<0.5 on
  // 0-1, i.e. <50 on 0-100) AND the supporting prose contained multiple
  // plausible candidates of the same type — the extractor probably
  // picked one of several possibilities and we want the user to
  // confirm which.
  'sce-ambiguous':
    'Extractor was uncertain (low confidence) and the prose mentions multiple plausible values — please confirm the right one.',
};

const GENERIC_REASON =
  'A quality rule flagged this claim — open the diff tab to review and adjudicate.';

/**
 * Map a raw `qualityWarnings[]` entry to its human-readable reason.
 * Forward-compat: unknown tags fall back to `GENERIC_REASON`.
 *
 * Exported for the regression suite so the copy contract is pinned.
 */
export function describeSceQualityWarning(tag: string): string {
  if (tag in QUALITY_WARNING_REASONS) {
    return QUALITY_WARNING_REASONS[tag as SceQualityWarning];
  }
  return GENERIC_REASON;
}

export interface SceQualityChipProps {
  /** Raw `claim.qualityWarnings` array. Renders nothing when empty / absent. */
  warnings: readonly string[] | undefined;
  /** Test-id prefix — typically the claim row's testid. */
  testIdPrefix: string;
}

/**
 * Amber dot + click-to-disclose chip. Returns `null` when `warnings`
 * is empty so callers can drop it into a row without conditional
 * rendering at the call site.
 *
 * The disclosure is a `<details>` element so the chip works without
 * a portal and is keyboard-accessible by default. Reasons are
 * de-duplicated so a claim with two `sce-cross-field` failures only
 * shows the copy once.
 */
export const SceQualityChip: React.FC<SceQualityChipProps> = ({ warnings, testIdPrefix }) => {
  if (!warnings || warnings.length === 0) return null;

  // De-dup reasons — a claim with two `sce-cross-field` rule failures
  // only shows the copy once.
  const reasons: string[] = [];
  const seen = new Set<string>();
  for (const w of warnings) {
    const r = describeSceQualityWarning(w);
    if (seen.has(r)) continue;
    seen.add(r);
    reasons.push(r);
  }

  return (
    <details
      className="inline-block align-middle shrink-0"
      data-testid={`${testIdPrefix}-quality-chip`}
      data-warnings={warnings.join(',')}
    >
      <summary
        className="cursor-pointer list-none inline-flex items-center gap-1 type-meta text-warning hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
        title={reasons.join(' • ')}
        aria-label={`Quality concern: ${reasons.join(' • ')}`}
        data-testid={`${testIdPrefix}-quality-chip-summary`}
      >
        {/* Amber dot — `text-warning` is the platform's standard
            amber-on-dark token; keeps a single source of truth for
            the colour. */}
        <span
          className="w-1.5 h-1.5 rounded-full bg-warning inline-block"
          aria-hidden="true"
        />
        <span>Quality</span>
      </summary>
      <ul
        className="mt-1 text-[10px] text-text-secondary border-l-2 border-warning/60 pl-2 space-y-0.5"
        data-testid={`${testIdPrefix}-quality-chip-reasons`}
      >
        {reasons.map((r) => (
          <li key={r}>{r}</li>
        ))}
      </ul>
    </details>
  );
};

export default SceQualityChip;
